import type {ClickAwayRefType} from '@spaced-out/ui-design-system';
import type {DataAuditWritebackType} from './types';

import * as React from 'react';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Card,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {FilterButtonOverlay} from '@spaced-out/ui-design-system/lib/components/FilterButtonOverlay';

import css from './data-audit-filters.css';


export const DATA_AUDIT_ALL_FILTER_KEY = 'all';

export type DataAuditAppliedFilters = {
  entity: string;
  note: string;
  source: string;
};

export const DATA_AUDIT_DEFAULT_FILTERS: DataAuditAppliedFilters = {
  entity: DATA_AUDIT_ALL_FILTER_KEY,
  note: DATA_AUDIT_ALL_FILTER_KEY,
  source: DATA_AUDIT_ALL_FILTER_KEY,
};

type FilterOption = {
  key: string;
  label: string;
};

type DataAuditFiltersProps = {
  writebackType: DataAuditWritebackType;
  entityOptions: Array<FilterOption>;
  noteOptions: Array<FilterOption>;
  sourceOptions: Array<FilterOption>;
  appliedFilters: DataAuditAppliedFilters;
  onApply: (filters: DataAuditAppliedFilters) => void;
};

const countActiveFilters = (
  writebackType: DataAuditWritebackType,
  filters: DataAuditAppliedFilters,
): number => {
  let count = 0;

  if (filters.source !== DATA_AUDIT_ALL_FILTER_KEY) {
    count += 1;
  }

  if (
    (writebackType === 'owb' || writebackType === 'fwb') &&
    filters.entity !== DATA_AUDIT_ALL_FILTER_KEY
  ) {
    count += 1;
  }

  if (writebackType === 'nwb' && filters.note !== DATA_AUDIT_ALL_FILTER_KEY) {
    count += 1;
  }

  return count;
};

const DataAuditFilters = ({
  writebackType,
  entityOptions,
  noteOptions,
  sourceOptions,
  appliedFilters,
  onApply,
}: DataAuditFiltersProps) => {
  const {t} = useLabelTranslation();
  const clickAwayRef = React.useRef<ClickAwayRefType>(null);

  const [draftEntity, setDraftEntity] = React.useState(appliedFilters.entity);
  const [draftNote, setDraftNote] = React.useState(appliedFilters.note);
  const [draftSource, setDraftSource] = React.useState(appliedFilters.source);

  React.useEffect(() => {
    setDraftEntity(appliedFilters.entity);
    setDraftNote(appliedFilters.note);
    setDraftSource(appliedFilters.source);
  }, [appliedFilters]);

  const showEntityFilter = writebackType === 'owb' || writebackType === 'fwb';
  const showNoteFilter = writebackType === 'nwb';

  const activeFilterCount = countActiveFilters(writebackType, appliedFilters);
  const filterButtonLabel =
    activeFilterCount > 0
      ? `${t('Filter', 'Filter')} (${activeFilterCount})`
      : t('Filter', 'Filter');

  return (
    <FilterButtonOverlay
      isFluid={false}
      type={activeFilterCount > 0 ? 'secondary' : 'tertiary'}
      buttonSize="medium"
      iconLeftName="bars-filter"
      clickAwayRef={clickAwayRef}
      anchorPosition="bottom-end"
      testId="data-audit-filters"
      buttonLabel={filterButtonLabel}
      classNames={{overlayContainer: css.overlayContainer}}
    >
      <Card>
        <CardContent className={css.filterCard}>
          {showEntityFilter && (
            <SimpleDropdown
              size="medium"
              classNames={{wrapper: css.filterDropdown}}
              label={t('Entity', 'Entity')}
              options={entityOptions}
              selectedKeys={[draftEntity]}
              onChange={({key}) => {
                setDraftEntity(key);
                onApply({
                  entity: key,
                  note: draftNote,
                  source: draftSource,
                });
              }}
            />
          )}

          {showNoteFilter && (
            <SimpleDropdown
              size="medium"
              classNames={{wrapper: css.filterDropdown}}
              label={t('Note', 'Note')}
              options={noteOptions}
              selectedKeys={[draftNote]}
              onChange={({key}) => {
                setDraftNote(key);
                onApply({
                  entity: draftEntity,
                  note: key,
                  source: draftSource,
                });
              }}
            />
          )}

          <SimpleDropdown
            size="medium"
            classNames={{wrapper: css.filterDropdown}}
            label={t('Sources', 'Sources')}
            options={sourceOptions}
            selectedKeys={[draftSource]}
            onChange={({key}) => {
              setDraftSource(key);
              onApply({
                entity: draftEntity,
                note: draftNote,
                source: key,
              });
            }}
          />
        </CardContent>
      </Card>
    </FilterButtonOverlay>
  );
};

export default DataAuditFilters;
